import { useEffect, useMemo, useState } from "react";
import { PRODUCTS } from "@/data/products";

const STORAGE_KEY = "vigo-cart";

const loadCart = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item) =>
          item &&
          PRODUCTS.some((p) => p.id === item.id) &&
          Number.isInteger(item.qty) &&
          item.qty >= 1
      )
      .map((item) => ({ id: item.id, qty: Math.min(item.qty, 99) }));
  } catch {
    return [];
  }
};

export function useCart() {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (id) =>
    setItems((prev) => {
      const found = prev.find((i) => i.id === id);
      if (found)
        return prev.map((i) => (i.id === id ? { ...i, qty: Math.min(i.qty + 1, 99) } : i));
      return [...prev, { id, qty: 1 }];
    });

  const setQty = (id, qty) =>
    setItems((prev) =>
      qty < 1
        ? prev.filter((i) => i.id !== id)
        : prev.map((i) => (i.id === id ? { ...i, qty: Math.min(qty, 99) } : i))
    );

  const remove = (id) => setItems((prev) => prev.filter((i) => i.id !== id));

  const detailed = useMemo(
    () =>
      items
        .map((i) => ({ ...i, product: PRODUCTS.find((p) => p.id === i.id) }))
        .filter((i) => i.product),
    [items]
  );

  const subtotal = useMemo(
    () => detailed.reduce((sum, i) => sum + i.product.price * i.qty, 0),
    [detailed]
  );

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  return { items: detailed, add, setQty, remove, subtotal, count };
}
